/**
 * IBAS motion tokens — the single source of truth for the *feel* of every
 * animation, shared across all three engines (GSAP, anime.js, Framer Motion) and
 * CSS. Durations and easings live here so the house rhythm stays consistent no
 * matter which engine a component reaches for.
 *
 * Design intent (Brand Guidelines §14 "Digital & UX behaviours"):
 *   - Motion is *restrained and meaningful* — it clarifies hierarchy and reveals
 *     content; it never auto-plays disruptively or competes for attention.
 *   - The signature curve is a weighty, confident ease-out (decelerate). Things
 *     arrive with Swiss composure — quick to commit, slow to settle.
 */

/** Seconds. GSAP/anime use seconds; Framer uses seconds; CSS multiplies ×1000. */
export const DURATION = {
  /** Hover/press state changes. */
  micro: 0.2,
  /** Small UI transitions (tabs, dropdown panels). */
  fast: 0.4,
  /** Default content reveal. */
  base: 0.6,
  /** Section-level reveals, larger elements. */
  slow: 0.9,
  /** Hero / signature cinematic moments. */
  cinematic: 1.2,
} as const;

/** cubic-bezier control tuples. Reused verbatim by every engine. */
export const EASE = {
  /** The house curve. Confident decelerate — use for 90% of entrances. */
  out: [0.16, 1, 0.3, 1],
  /** Symmetric — use for moves that leave and return (parallax, marquee wraps). */
  inOut: [0.65, 0, 0.35, 1],
  /** Accelerate — use only for exits. */
  in: [0.5, 0, 0.75, 0],
  /** Restrained overshoot for tactile controls (magnetic, press). Keep subtle. */
  spring: [0.34, 1.4, 0.64, 1],
} as const;

export type EaseTuple = readonly [number, number, number, number];

/** cubic-bezier(...) string for CSS transitions / Web Animations API. */
export function cssEase(e: EaseTuple = EASE.out): string {
  return `cubic-bezier(${e[0]}, ${e[1]}, ${e[2]}, ${e[3]})`;
}

/** SVG path form for GSAP CustomEase.create(). */
export function easePath(e: EaseTuple = EASE.out): string {
  return `M0,0 C${e[0]},${e[1]} ${e[2]},${e[3]} 1,1`;
}

/** Named GSAP CustomEase ids registered in lib/gsap.ts. */
export const GSAP_EASE = {
  out: 'ibas-out',
  inOut: 'ibas-in-out',
  in: 'ibas-in',
  spring: 'ibas-spring',
} as const;

/** Stagger steps (seconds) for sequenced reveals. */
export const STAGGER = {
  tight: 0.04,
  base: 0.08,
  loose: 0.14,
} as const;

/** Default distance (px) elements travel on a rise-in reveal. */
export const RISE_DISTANCE = 28;
